import type {
  CalibrationRecord,
  DispenseRecord,
  EnvSensorSnapshot,
  ExportJob,
  ModuleEndpoint,
  ReagentLot,
  Recipe,
  Sample,
  SampleReport,
  WasteContainer,
} from '@ctcells/contract'

import { orchestrator } from '../clients.ts'
import { keys } from '../keys.ts'
import { hasMore, normalizeList, type Listed } from '../page.ts'
import { createQuery, queryParams } from '../query.ts'
import type { PageArgs } from './ops.ts'

/** 리소스 도메인 조회 — 시료 · 레시피 · 랩웨어 · 시약 · 폐기통 · 환경센서 · 보정 · 분주 · 내보내기 */

/**
 * ⚠ **실물이 벌거벗은 배열을 주고 `total_count` 가 없다**(실측). 가상 스크롤 끝 판정은
 *   `hasMore()` 의 추론에 기댄다 — 깨끗하지 않다(백엔드 보고 대상 J).
 * ⚠ **검색·상태 필터 파라미터가 계약에 0건이다.** 시료 목록의 검색창은 서버로 못 넘긴다.
 */
export const useSamples = createQuery<PageArgs, Listed<Sample>>({
  key: (args) => keys.samples.list(args),
  fetch: async (args) =>
    normalizeList<Sample>(
      await orchestrator.request({ method: 'GET', path: '/samples', query: queryParams(args) }),
      args,
    ),
})

/** `GET /samples` 의 한 쪽 상한 — 서버 `Query(le=500)`(`app/views/samples.py`). 넘기면 422 다 */
const SAMPLES_PAGE_MAX = 500
/** 쪽 넘김 안전핀 — 이보다 많이 도는 것은 무한 루프다(시료 25,000건 이상은 이 장비의 현실이 아니다) */
const SAMPLES_PAGE_CAP = 50

/**
 * **시료 전부** — 서버 상한(500)씩 끝까지 넘겨 한 목록으로 잇는다.
 *
 * 🔴 **왜 있나**(2026-09-22 실물 사고 — eric 「테스트 작업을 했는데 챔버에 아무것도 없어?」). 서버가 `sample_id`
 *    순으로 주고 화면은 앞 200건만 받았다. 시료가 269건이 되자 도는 작업의 시료(`TEST-20260922-1501-*`)가
 *    201번째 뒤로 밀려 **한 건도 화면에 안 왔다** — 챔버 표·환자 카드·작업 상세가 다 비었다.
 *    「지금 도는 작업의 시료」를 찾는 화면은 목록의 «끝»에 있는 것을 봐야 하므로 한 쪽으로는 안 된다.
 * ⚠ `batch_id` 필터가 계약에 없어(`GET /samples` 파라미터 `limit`·`offset` 둘뿐) 전부 받아 화면이 고른다.
 *   서버 필터가 생기면 이 훅을 그 필터로 바꾼다(백엔드 요청 건).
 * ⚠ 무효화는 `keys.samples.all()` 아래라 구독(`sample_progress`·`sample_lifecycle`)이 종전대로 갱신한다.
 */
export const useAllSamples = createQuery<PageArgs, Listed<Sample>>({
  key: () => keys.samples.list({ scope: 'all' }),
  fetch: async () => {
    const items: Sample[] = []
    for (let page = 0; page < SAMPLES_PAGE_CAP; page += 1) {
      const requested = { limit: SAMPLES_PAGE_MAX, offset: page * SAMPLES_PAGE_MAX }
      const listed = normalizeList<Sample>(
        await orchestrator.request({
          method: 'GET',
          path: '/samples',
          query: queryParams(requested),
        }),
        requested,
      )
      items.push(...listed.items)
      if (!hasMore(listed)) break
    }
    return { items, total_count: items.length, limit: null, offset: null }
  },
})

export const useSample = createQuery<{ sampleId: string | undefined }, Sample>({
  key: ({ sampleId }) => keys.samples.detail(sampleId ?? ''),
  fetch: ({ sampleId }) =>
    orchestrator.request<Sample>({ method: 'GET', path: `/samples/${String(sampleId)}` }),
  enabled: ({ sampleId }) => sampleId !== undefined && sampleId !== '',
})

export const useSampleReport = createQuery<{ sampleId: string | undefined }, SampleReport>({
  key: ({ sampleId }) => keys.reports.detail(sampleId ?? ''),
  fetch: ({ sampleId }) =>
    orchestrator.request<SampleReport>({ method: 'GET', path: `/reports/${String(sampleId)}` }),
  enabled: ({ sampleId }) => sampleId !== undefined && sampleId !== '',
})

export interface RecipeListArgs extends PageArgs {
  status?: Recipe['status']
}

/**
 * ⚠ 목록이 **`payload` 전문을 통째로** 실어 온다 — 실물 13건에 401KB 다(실측).
 *   목록 화면이 본문을 안 쓰므로 나중에 서버 축약(`fields=`)을 요청할 자리다.
 */
export const useRecipes = createQuery<RecipeListArgs, Listed<Recipe>>({
  key: (args) => keys.recipes.list(args),
  fetch: async (args) =>
    normalizeList<Recipe>(
      await orchestrator.request({ method: 'GET', path: '/recipes', query: queryParams(args) }),
      args,
    ),
})

export const useRecipe = createQuery<{ recipeId: string | undefined }, Recipe>({
  key: ({ recipeId }) => keys.recipes.detail(recipeId ?? ''),
  fetch: ({ recipeId }) =>
    orchestrator.request<Recipe>({ method: 'GET', path: `/recipes/${String(recipeId)}` }),
  enabled: ({ recipeId }) => recipeId !== undefined && recipeId !== '',
})

/** ⚠ 실물 0건(빈 컬렉션). 표가 0행이면 「빈 목록」이 정답이다 — 오류가 아니다 */
export const useReagentLots = createQuery<{ opened?: boolean }, Listed<ReagentLot>>({
  key: (args) => keys.reagentLots.list(args),
  fetch: async (args) =>
    normalizeList<ReagentLot>(
      await orchestrator.request({
        method: 'GET',
        path: '/reagents/lots',
        query: queryParams(args),
      }),
    ),
})

/** ⚠ 실물 0건 */
export const useWasteContainers = createQuery<PageArgs, Listed<WasteContainer>>({
  key: (args) => keys.wasteContainers.list(args),
  fetch: async (args) =>
    normalizeList<WasteContainer>(
      await orchestrator.request({
        method: 'GET',
        path: '/waste-containers',
        query: queryParams(args),
      }),
      args,
    ),
})

/** ⚠ 실물 0건 */
export const useEnvSensors = createQuery<PageArgs, Listed<EnvSensorSnapshot>>({
  key: (args) => keys.envSensors.list(args),
  fetch: async (args) =>
    normalizeList<EnvSensorSnapshot>(
      await orchestrator.request({ method: 'GET', path: '/env-sensors', query: queryParams(args) }),
      args,
    ),
})

/** ⚠ 실물 0건 */
export const useCalibrations = createQuery<
  PageArgs & { target?: string },
  Listed<CalibrationRecord>
>({
  key: (args) => keys.calibrations.list(args),
  fetch: async (args) =>
    normalizeList<CalibrationRecord>(
      await orchestrator.request({
        method: 'GET',
        path: '/calibrations',
        query: queryParams(args),
      }),
      args,
    ),
})

/** ⚠ 실물 0건 */
export const useDispenseRecords = createQuery<
  PageArgs & { batch_id?: string; sample_id?: string; reagent_lot?: string },
  Listed<DispenseRecord>
>({
  key: (args) => keys.dispenseRecords.list(args),
  fetch: async (args) =>
    normalizeList<DispenseRecord>(
      await orchestrator.request({
        method: 'GET',
        path: '/dispense-records',
        query: queryParams(args),
      }),
      args,
    ),
})

export const useExports = createQuery<PageArgs, Listed<ExportJob>>({
  key: (args) => keys.exports.list(args),
  fetch: async (args) =>
    normalizeList<ExportJob>(
      await orchestrator.request({ method: 'GET', path: '/exports', query: queryParams(args) }),
      args,
    ),
})

export const useExportJob = createQuery<{ exportId: string | undefined }, ExportJob>({
  key: ({ exportId }) => keys.exports.detail(exportId ?? ''),
  fetch: ({ exportId }) =>
    orchestrator.request<ExportJob>({ method: 'GET', path: `/exports/${String(exportId)}` }),
  enabled: ({ exportId }) => exportId !== undefined && exportId !== '',
})

/**
 * 내보내기 파일 주소.
 *
 * ✅ **`download_url` 은 실물이 준다**(2026-09-22 재측 — `ready` 응답에
 *   `"/api/v1/exports/exp-…/download"`). 그런데도 이 함수가 남는 까닭은 **접두 때문**이다:
 *   서버가 주는 값은 `/api/v1` 을 포함한 절대 경로이고 `orchestrator.request` 는 접두를 스스로
 *   붙인다. 받은 값을 그대로 넣으면 `/api/v1/api/v1/…` 이 된다.
 * ⚠ 무인증 정적 URL 이 아니다 — Bearer 가 필요해서 `<a href>` 로 바로 못 연다.
 *   플랫폼 파일 포트(`platform.files.save`)로 받아 저장한다(`useExportFile`).
 */
export function exportDownloadPath(exportId: string): string {
  return `/exports/${exportId}/download`
}

/**
 * 모듈 **한 대**의 주소 — `GET /modules/{id}/endpoint`.
 *
 * 🔴 **주소가 목록 응답(`/modules`)에 없다.** 그래서 이 경로가 따로 있다(2026-09-18 실측).
 *    목록에 `host`·`port` 를 같이 실어주면 이 훅은 사라진다(`docs/backend-requests.csv` BR-14).
 * 🔴 **스냅샷 1회다. 주기 폴링이 아니다**(DP-01).
 *
 * 🔴 **복수형(`useModuleEndpoints`)을 되살리지 마라**(2026-09-21 제거). 모듈 목록을
 *    `Promise.allSettled` 로 한꺼번에 부르고 **성공한 것만 담아** 돌려주던 훅이었다. 그 자리는
 *    ⓐ 모듈 수만큼 호출이 느는 N+1 이었고, ⓑ 무엇보다 **실패를 조회 계층에서 성공으로
 *    바꿨다** — 이 경로는 **admin 전용**이라(`modules.py:105 ENDPOINT_ROLES`) operator 는
 *    전부 403 인데도 쿼리가 「성공 + 빈 객체」가 됐고, 화면에는 `—` 만 떴다. 「값이 없다」와
 *    「볼 권한이 없다」를 가를 자리가 **아예 없어진다.** 여러 대의 주소가 정말 필요해지면
 *    BR-14 로 **목록 응답에 실어 달라고 요청**하지, 실패를 삼키는 합성 훅을 다시 만들지 마라.
 */
export const useModuleEndpoint = createQuery<{ moduleId: string | undefined }, ModuleEndpoint>({
  key: ({ moduleId }) => [...keys.modules.detail(moduleId ?? ''), 'endpoint'],
  fetch: ({ moduleId }) =>
    orchestrator.request<ModuleEndpoint>({
      method: 'GET',
      path: `/modules/${String(moduleId)}/endpoint`,
    }),
  enabled: ({ moduleId }) => moduleId !== undefined && moduleId !== '',
})
